<template>
  <div class="modal-overlay">
    <!-- Modal Popup For Show Item Prices -->
    <div class="modal-content">
      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-spinner">
        <span class="spinner-border" role="status" aria-hidden="true" style="width: 3rem; height: 3rem; border-width: 0.25em;"></span>
        <div>Loading prices...</div>
      </div>

      <template v-else>
        <div class="modal-header">
          <div class="title-container">
            <h4 class="title">Item: {{ modalHeader }}</h4>
            <div class="subtitle-text" v-if="sapCode">SAP Code: {{ sapCode }}</div>
          </div>
          <div class="modal-header-buttons">
            <!-- Pending state buttons -->
            <template v-if="itemPriceData.status === '0' && allowApprove">
              <button class="reject-button rejected" @click="handleAction('Reject')">Reject</button>
              <button class="approve-button approved" @click="handleAction('Approve')">Approve</button>
            </template>

            <!-- Prices Reject Button -->
            <button
                v-if="itemPriceData.status === '2'"
                class="reject-button rejected"
                @click="handleAction('Reject')">
              <span>Item Price Rejected</span>
            </button>

            <!-- Prices Approve Button -->
            <button
                v-if="itemPriceData.status === '1'"
                class="approve-button approved"
                @click="handleAction('Approve')">
              <span>Item Price Approved</span>
            </button>

            <!-- Popup Close Button -->
            <button class="close-button" @click="closeModal">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        <!-- Table to Show Data -->
        <div v-if="itemPriceList.length > 0" class="table-wrapper">
          <div class="table-container">
            <table class="data-table">
              <thead>
              <tr>
                <th>District</th>
                <th>Cost (Rs.)</th>
                <th class="bg-distributor-1">New Distributor w/o VAT</th>
                <th class="bg-distributor-2">Distributor VAT</th>
                <th class="bg-distributor-3">Distributor Price w/ VAT</th>
                <th class="bg-dealer-1">New Dealer w/o VAT</th>
                <th class="bg-dealer-2">Dealer VAT</th>
                <th class="bg-dealer-3">Dealer Price w/ VAT</th>
                <th class="bg-consumer-1">New Consumer w/o VAT</th>
                <th class="bg-consumer-2">Consumer VAT</th>
                <th class="bg-consumer-3">Consumer Price w/ VAT</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(price, index) in itemPriceList" :key="index">
                <td>{{ price.district_name }}</td>
                <td class="text-right">{{ formatCurrency(price.cost_price) }}</td>
                <td class="bg-distributor-1 text-right">{{ formatCurrency(price.distributor_price_without_vat) }}</td>
                <td class="bg-distributor-2 text-right">{{ formatCurrency(price.distributor_vat_amount) }}</td>
                <td class="bg-distributor-3 text-right">{{ formatCurrency(price.distributor_price_with_vat) }}</td>
                <td class="bg-dealer-1 text-right">{{ formatCurrency(price.dealer_price_without_vat) }}</td>
                <td class="bg-dealer-2 text-right">{{ formatCurrency(price.dealer_vat_amount) }}</td>
                <td class="bg-dealer-3 text-right">{{ formatCurrency(price.dealer_price_with_vat) }}</td>
                <td class="bg-consumer-1 text-right">{{ formatCurrency(price.consumer_price_without_vat) }}</td>
                <td class="bg-consumer-2 text-right">{{ formatCurrency(price.consumer_vat_amount) }}</td>
                <td class="bg-consumer-3 text-right">{{ formatCurrency(price.consumer_price_with_vat) }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Show when no data and not loading -->
        <div v-else class="price-not-available-wrapper">
          <div class="price-not-available">
            <img src="../../assets/vectors/data_not_found.png" alt="Prices Not Available" class="price-not-available-img" />
            <p class="price-not-available-message">No item prices available.</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Bottom Sheet for Approve or Reject -->
    <div v-if="showBottomSheetModal" class="bottom-sheet-overlay">
      <div class="bottom-sheet-content">
        <div class="bottom-sheet-header">
          <h4>{{ bottomSheetAction }} Item Prices?</h4>
        </div>

        <!-- Textarea for Remark pending action -->
        <div v-if="this.itemPriceData.status === '0'">
          <!-- Enter Remark -->
          <textarea
              v-model="remark"
              class="bottom-sheet-textarea"
              placeholder="Add your remark here..."
              maxlength="500"
          ></textarea>

          <!-- Character Counter -->
          <div class="character-counter">
            {{ remark.length }}/500
          </div>
        </div>

        <!-- Show Item Prices if Approved or Rejected details -->
        <div v-else class="price-action-details">
          <table>
            <tr>
              <td>Price Valid From</td>
              <td>{{ this.itemPriceData.price_valid_from }}</td>
            </tr>
            <tr>
              <td>Created By</td>
              <td>{{ this.itemPriceData.create_by_name || this.itemPriceData.created_by }}</td>
            </tr>
            <tr>
              <td>Created At</td>
              <td>{{ formatDateTime(this.itemPriceData.created_at) }}</td>
            </tr>
            <tr>
              <td>{{getLabel(bottomSheetAction)}} By</td>
              <td>{{ this.itemPriceData.confirmed_by_name || this.itemPriceData.confirmed_by || 'N/A' }}</td>
            </tr>
            <tr>
              <td>{{getLabel(bottomSheetAction)}} At</td>
              <td>{{ formatDateTime(this.itemPriceData.confirmed_at) }}</td>
            </tr>
            <tr>
              <td>Remark</td>
              <td>{{ this.itemPriceData.remark || 'No remark provided' }}</td>
            </tr>
          </table>
        </div>

        <!-- Confirm and Cancel Buttons -->
        <div class="bottom-sheet-buttons">
          <button class="exit-button" @click="closeBottomSheet">Exit</button>
          <button
              v-if="this.itemPriceData.status === '0'"
              class="action-button"
              :style="{ backgroundColor: bottomSheetAction === 'Approve' ? '#4CAF50' : '#f44336' }"
              @click="handleConfirm"
          >
            <span v-if="loadingAction" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-else>{{ bottomSheetAction }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { viewItemPrices, itemPriceConfirm } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  props: {
    itemPriceId: {
      type: String,
      required: true
    },
    allowApprove: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'refresh'],

  data() {
    return {
      // Get the API access token from the local storage
      token: localStorage.getItem('token_set'),

      // Get the user profile id from the local storage
      profileId: localStorage.getItem('profile_id'),

      // Controls visibility of the loading until indicate item price data on the pop-up
      loading: false,

      // Controls visibility of the loading until indicate the item price approve or reject API call
      loadingAction: false,

      // Controls visibility of the bottom sheet for approve or reject item price
      showBottomSheetModal: false,

      // Initialize the item price view popup modal header (this is dynamic)
      modalHeader: "",
      sapCode: "",

      // Initialize the item price list array to fetch item price as formatted
      itemPriceList: [],

      // Initialize the item price data to fetch from API
      itemPriceData: {},

      // Initialize bottom sheet action name (Approve/Reject) depend on the selected button in the header
      bottomSheetAction: '',

      // Initialize approve/reject remark value
      remark: '',
    }
  },

  mounted() {
    this.fetchItemPrices();
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  methods: {
    // Format DateTime as "YYYY-MM-DD | HH:MM:SS"
    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";

      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "N/A";

      const formattedDate = date.toLocaleDateString("en-CA").replace(/\//g, "-");
      const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true };
      const formattedTime = date.toLocaleTimeString("en-US", timeOptions);

      return `${formattedDate} | ${formattedTime}`;
    },

    formatCurrency(value) {
      const num = parseFloat(value);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // View Item Prices
    async fetchItemPrices() {
      try {
        this.loading = true;
        const response = await viewItemPrices(this.itemPriceId, this.token);

        if (response.data && response.data.status === "success") {
          const dataContent = response.data.data;
          this.itemPriceData = {
            id: dataContent.id,
            item_name: dataContent.item_name,
            price_valid_from: dataContent.price_valid_from,
            create_by_name: dataContent.create_by_name,
            created_by: dataContent.created_by,
            created_at: dataContent.created_at,
            confirmed_by: dataContent.confirmed_by,
            confirmed_by_name: dataContent.confirmed_by_name,
            confirmed_at: dataContent.confirmed_at,
            status: dataContent.status,
            remark: dataContent.remark
          };

          this.itemPriceList = dataContent.item_prices || [];
          this.modalHeader = dataContent.item_name || '';
          this.sapCode = dataContent.sap_code || '';
        } else {
          this.toast.error("Failed to fetch item prices.");
        }
      } catch (e) {
        console.error("Error fetching item prices", e);
      } finally {
        this.loading = false;
      }
    },

    // Close Modal
    closeModal() {
      this.$emit('close');
    },

    // Close the bottom sheet
    closeBottomSheet() {
      this.showBottomSheetModal = false;
      this.remark = '';
    },

    // Show action bottom sheet
    handleAction(action) {
      this.showBottomSheet(action);
    },

    // Bottom sheet setup
    showBottomSheet(action) {
      this.bottomSheetAction = action;
      this.showBottomSheetModal = true;
    },

    // Approve or Reject Item Prices
    async handleConfirm() {
      try {
        this.loadingAction = true;

        if (this.remark === "") {
          this.toast.error("Please add remark here.");
          return;
        }

        const payload = {
          confirmed_status: this.bottomSheetAction === 'Approve' ? 1 : 2,
          confirmed_by: this.profileId,
          remark: this.remark
        };

        const response = await itemPriceConfirm(this.itemPriceId, payload, this.token);

        if (response.data && response.data.status === "success") {
          this.toast.success(`Item prices ${this.bottomSheetAction.toLowerCase()}d successfully.`);
          this.closeBottomSheet();
          this.$emit('refresh');
          this.closeModal();
        } else {
          this.toast.error(response.data.message || "Failed to process request.");
        }
      } catch (e) {
        console.error("Error processing approval/rejection:", e);
        this.toast.error("Something went wrong. Please try again.");
      } finally {
        this.loadingAction = false;
      }
    },

    // Show Details Append
    getLabel(bottomSheetAction) {
      if (bottomSheetAction === "Approve" || this.itemPriceData.status === '1') {
        return "Approved";
      } else {
        return "Rejected";
      }
    }
  },
}
</script>

<style scoped>
/* Styling for popup modal show */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Styling for popup modal content */
.modal-content {
  background-color: white;
  width: 100%;
  max-width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

/* Styling for popup modal header */
.modal-header {
  background-color: #5A6166;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 1px solid #ddd;
}

/* Styling for popup modal header title */
.title-container {
  padding: 15px 0 15px 20px;
}
.title {
  color: #fff;
  margin: 0;
  font-size: 1.25rem;
}
.subtitle-text {
  color: #d1d5db;
  font-size: 13px;
  margin-top: 4px;
}

.table-wrapper {
  flex-grow: 1;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Table Container */
.table-container {
  flex-grow: 1;
  overflow: auto;
  position: relative;
}

/* Table */
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.data-table th, .data-table td {
  padding: 8px 12px;
  border: 1px solid #ddd;
  text-align: left;
  white-space: nowrap;
}

.data-table td.text-right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Make the table header sticky */
.data-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f8f9fa;
  color: #444;
  font-weight: 600;
}

/* Distributor - Green */
.bg-distributor-1 { background-color: #f2fcf5; }
.bg-distributor-2 { background-color: #e6f9ec; }
.bg-distributor-3 { background-color: #d1f2dc; }
th.bg-distributor-1 { background-color: #e6f9ec !important; }
th.bg-distributor-2 { background-color: #d1f2dc !important; }
th.bg-distributor-3 { background-color: #bcebc7 !important; }

/* Dealer - Blue */
.bg-dealer-1 { background-color: #f0f7ff; }
.bg-dealer-2 { background-color: #e0efff; }
.bg-dealer-3 { background-color: #cce5ff; }
th.bg-dealer-1 { background-color: #e0efff !important; }
th.bg-dealer-2 { background-color: #cce5ff !important; }
th.bg-dealer-3 { background-color: #b3d7ff !important; }

/* Consumer - Yellow */
.bg-consumer-1 { background-color: #fffdf0; }
.bg-consumer-2 { background-color: #fffad6; }
.bg-consumer-3 { background-color: #fff5b8; }
th.bg-consumer-1 { background-color: #fffad6 !important; }
th.bg-consumer-2 { background-color: #fff5b8 !important; }
th.bg-consumer-3 { background-color: #ffef99 !important; }


/* Styling the loading in the modal popup */
.loading-spinner {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  font-size: 18px;
  color: #007bff;
}

.loading-spinner div {
  margin-top: 15px;
}

/* Styling modal header buttons */
.modal-header-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  padding-right: 20px;
}

/* Styling approve and reject buttons */
.approve-button, .reject-button, .close-button {
  padding: 6px 12px;
  font-size: 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.reject-button {
  background-color: #fff;
  color: #f44336;
  border: 1px solid #f44336;
  font-weight: 500;
}

.approve-button {
  background-color: #fff;
  color: #4CAF50;
  border: 1px solid #4CAF50;
  font-weight: 500;
}

/* Styling reject button colors when rejected */
.reject-button.rejected {
  border: 1px solid white;
  background-color: #f44336;
  color: white;
}

/* Styling approve button colors when approved */
.approve-button.approved {
  border: 1px solid white;
  background-color: #4CAF50;
  color: white;
}

/* Styling for popup modal close button */
.close-button {
  background-color: transparent;
  color: white;
  border: none;
  font-size: 18px;
  margin-left: 10px;
}
.close-button:hover {
  color: #ffcccc;
}

/* Bottom Sheet Overlay */
.bottom-sheet-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1100;
}

/* Bottom Sheet Content */
.bottom-sheet-content {
  background-color: white;
  width: 100%;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 20px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  max-width: 1400px;
}

/* Bottom Sheet Header */
.bottom-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Bottom Sheet Textarea */
.bottom-sheet-textarea {
  width: 100%;
  height: 100px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;
  margin-bottom: 20px;
}

/* Bottom Sheet Textarea when typing */
.bottom-sheet-textarea:focus {
  border-color: #1c1f23;
  outline: none;
}

/* Bottom Sheet Buttons */
.bottom-sheet-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Styling exit and action buttons */
.exit-button, .action-button {
  padding: 8px 20px;
  font-size: 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* Styling exit button colors */
.exit-button {
  background-color: #5A6166;
  color: white;
}

/* Styling action button colors */
.action-button {
  background-color: #4CAF50;
  color: white;
}

/* Styling item price approve or reject details table */
.price-action-details table {
  margin: 10px 0 20px 0;
  width: 100%;
  border-collapse: collapse;
}

/* Styling item price approve or reject details table data */
.price-action-details td {
  padding: 10px;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  color: #333;
}

/* Styling item price approve or reject details table first column */
.price-action-details td:first-child {
  font-weight: bold;
  width: 30%;
  color: #555;
}

/* Show error message when prices are not available */
.price-not-available-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
}

.price-not-available {
  text-align: center;
}

.price-not-available-img {
  max-width: 300px;
  width: 100%;
  height: auto;
}

.price-not-available-message {
  font-size: 1.2rem;
  color: #666;
  font-weight: bold;
  margin-top: 10px;
}

/* Character Counter */
.character-counter {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-top: -15px;
  margin-bottom: 15px;
}
</style>